import React from 'react';
import { resolveEstimateDescriptionInline } from '../../utils/estimate-description';
import { AccountDescriptionSettings } from '../../utils/description-settings';

interface CustomOrderDescriptionSummaryTabProps {
    salesDescriptionSettingsMapped: { id: string; isEnabled: boolean }[];
    draperyItemDetail?: {
        pleatTypesWithPricing: { id: any; name: string }[];
    }[];
    data: any;
    editMode: boolean;
    descriptionSettings?: AccountDescriptionSettings | null;
}

export const CustomOrderDescriptionSummaryTab: React.FC<CustomOrderDescriptionSummaryTabProps> = ({
    salesDescriptionSettingsMapped,
    draperyItemDetail,
    data,
    editMode,
    descriptionSettings,
}) => {
    const styleDescriptionRaw = data?.draperyOrderDesign?.description;
    const styleDescription =
        typeof styleDescriptionRaw === "string" ? styleDescriptionRaw.trim() : "";

    const isSettingEnabled = (id: string) =>
        salesDescriptionSettingsMapped.some(item => item.id === id && item.isEnabled);
    const isLiningNoEnabled = isSettingEnabled("saleslining:no");
    const isLiningYesEnabled = isSettingEnabled("saleslining:yes");
    const isShowLiningNameEnabled = isSettingEnabled("salesshowliningname");
    const isLiningPurchaserIfComEnabled = isSettingEnabled("salesliningpurchaserifcom");
    const isLiningYardsIfComEnabled = isSettingEnabled("salesliningyardsifcom");
    const isLiningYardsIfWorkroomEnabled = isSettingEnabled("salesliningyardsifworkroompurchasing");
    const isInterliningNoEnabled = isSettingEnabled("salesinterlining:no");
    const isInterliningYesEnabled = isSettingEnabled("salesinterlining:yes");
    const isShowInterliningNameEnabled = isSettingEnabled("salesshowinterliningname");
    const isInterliningPurchaserIfComEnabled = isSettingEnabled("salesinterliningpurchaserifcom");
    const isInterliningYardsIfComEnabled = isSettingEnabled("salesinterliningyardsifcom");
    const isInterliningYardsIfWorkroomEnabled = isSettingEnabled("salesinterliningyardsifworkroompurchasing");

    const liningPatternItems = data.liningsPatternItems || [];
    const liningItem = data.liningsList.find((l: any) => l.name === "Lining" && l.active)
    const liningOrderOrigin = liningItem && liningItem.draperyOrderLiningsFabric && liningItem.draperyOrderLiningsFabric.orderOrigin
    const liningItemName = resolveEstimateDescriptionInline({
        fabric: liningItem && liningItem.draperyOrderLiningsFabric,
        items: liningPatternItems,
        fallbackMode: "pattern-only",
        descriptionSettings,
    })
    const liningYardsNeeded = liningItem && liningItem.draperyOrderLiningsFabric && liningItem.draperyOrderLiningsFabric.yardsNeeded
    const interliningItem = data.liningsList.find((l: any) => l.name === "Interlining" && l.active)
    const interliningOrderOrigin = interliningItem && interliningItem.draperyOrderLiningsFabric && interliningItem.draperyOrderLiningsFabric.orderOrigin
    const interliningItemName = resolveEstimateDescriptionInline({
        fabric: interliningItem && interliningItem.draperyOrderLiningsFabric,
        items: liningPatternItems,
        fallbackMode: "pattern-only",
        descriptionSettings,
    })
    const interliningYardsNeeded = interliningItem && interliningItem.draperyOrderLiningsFabric && interliningItem.draperyOrderLiningsFabric.yardsNeeded

    const hasLiningItem = Boolean(liningItem);
    const hasInterliningItem = Boolean(interliningItem);
    const shouldShowLiningSection =
        isLiningNoEnabled ||
        isLiningYesEnabled ||
        isShowLiningNameEnabled ||
        isLiningPurchaserIfComEnabled ||
        isLiningYardsIfComEnabled ||
        isLiningYardsIfWorkroomEnabled;
    const shouldShowInterliningSection =
        isInterliningNoEnabled ||
        isInterliningYesEnabled ||
        isShowInterliningNameEnabled ||
        isInterliningPurchaserIfComEnabled ||
        isInterliningYardsIfComEnabled ||
        isInterliningYardsIfWorkroomEnabled;

    const shouldShowLiningYes = hasLiningItem && isLiningYesEnabled;
    const shouldShowLiningName = hasLiningItem && isShowLiningNameEnabled && Boolean(liningItemName);
    const shouldShowLiningCom = hasLiningItem && liningOrderOrigin === 2 && isLiningPurchaserIfComEnabled;
    const shouldShowLiningYardsFromCom = hasLiningItem && liningOrderOrigin === 2 && isLiningYardsIfComEnabled && Boolean(liningYardsNeeded);
    const shouldShowLiningYardsFromWorkroom = hasLiningItem && liningOrderOrigin === 1 && isLiningYardsIfWorkroomEnabled && Boolean(liningYardsNeeded);
    const shouldShowLiningYards = shouldShowLiningYardsFromCom || shouldShowLiningYardsFromWorkroom;
    const liningDetailParts: string[] = [];
    if (hasLiningItem) {
        if (shouldShowLiningYes) {
            liningDetailParts.push("Yes");
        }
        if (shouldShowLiningName) {
            liningDetailParts.push(liningItemName);
        }
        if (shouldShowLiningYards) {
            liningDetailParts.push(`${liningYardsNeeded} yards`);
        }
    } else if (isLiningNoEnabled) {
        liningDetailParts.push("No");
    }
    const shouldRenderLining =
        shouldShowLiningSection &&
        ((hasLiningItem && (liningDetailParts.length > 0 || shouldShowLiningCom)) ||
            (!hasLiningItem && liningDetailParts.length > 0));

    const shouldShowInterliningYes = hasInterliningItem && isInterliningYesEnabled;
    const shouldShowInterliningName = hasInterliningItem && isShowInterliningNameEnabled && Boolean(interliningItemName);
    const shouldShowInterliningCom = hasInterliningItem && interliningOrderOrigin === 2 && isInterliningPurchaserIfComEnabled;
    const shouldShowInterliningYardsFromCom = hasInterliningItem && interliningOrderOrigin === 2 && isInterliningYardsIfComEnabled && Boolean(interliningYardsNeeded);
    const shouldShowInterliningYardsFromWorkroom = hasInterliningItem && interliningOrderOrigin === 1 && isInterliningYardsIfWorkroomEnabled && Boolean(interliningYardsNeeded);
    const shouldShowInterliningYards = shouldShowInterliningYardsFromCom || shouldShowInterliningYardsFromWorkroom;
    const interliningDetailParts: string[] = [];
    if (hasInterliningItem) {
        if (shouldShowInterliningYes) {
            interliningDetailParts.push("Yes");
        }
        if (shouldShowInterliningName) {
            interliningDetailParts.push(interliningItemName);
        }
        if (shouldShowInterliningYards) {
            interliningDetailParts.push(`${interliningYardsNeeded} yards`);
        }
    } else if (isInterliningNoEnabled) {
        interliningDetailParts.push("No");
    }
    const shouldRenderInterlining =
        shouldShowInterliningSection &&
        ((hasInterliningItem && (interliningDetailParts.length > 0 || shouldShowInterliningCom)) ||
            (!hasInterliningItem && interliningDetailParts.length > 0));

    return (
        <>
            <>
                {styleDescription && (
                    <>
                        <span className="nonBold" style={{ whiteSpace: "pre-wrap" }}>
                            {styleDescription}
                        </span>
                        <br />
                    </>
                )}
                {shouldRenderLining && (
                    <>
                        {shouldShowLiningCom &&
                            <span className="boldText">
                                {`COM `}
                            </span>
                        }
                        <span className="boldText">
                            Lining:
                        </span>
                        {liningDetailParts.length > 0 && (
                            <span style={{ paddingLeft: "8px", fontWeight: "400" }}>
                                {liningDetailParts.join(", ")}
                            </span>
                        )}
                        <br />
                    </>
                )}
                {shouldRenderInterlining && (
                    <>
                        {shouldShowInterliningCom &&
                            <span className="boldText">
                                {`COM `}
                            </span>
                        }
                        <span className="boldText">
                            Interlining:
                        </span>
                        {interliningDetailParts.length > 0 && (
                            <span style={{ paddingLeft: "8px", fontWeight: "400" }}>
                                {interliningDetailParts.join(", ")}
                            </span>
                        )}
                        <br />
                    </>
                )}
            </>
        </>
    );
};
